import {
  Checkbox, Col, Row, Select, Space, Spin, Typography
} from 'antd';
import React, { useEffect, useMemo, useRef } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DropdownElement } from 'common/components/DropdownType';
import Input from 'common/components/Input';
import { ServicePackageAddOnData } from 'common/services/packages/types';
import { packageDurations } from 'extends/PackageDetail';

interface PackageGeneralInfoProps {
  method: UseFormReturn<any>;
  addOnsPackageData?: ServicePackageAddOnData[];
  isLoading?: boolean;
}

const PackageGeneralInfo = ({ isLoading, method, addOnsPackageData }: PackageGeneralInfoProps) => {
  const { t } = useTranslation();
  const addOnsOptions = useMemo(() => addOnsPackageData?.map((item) => ({
    label: item.name,
    value: item.id,
  })), [addOnsPackageData]);

  const durationValue = useWatch({ control: method.control, name: 'duration' });

  const prevDurationRef = useRef(durationValue);

  useEffect(() => {
    const current = durationValue;
    if (prevDurationRef?.current?.value !== current?.value && prevDurationRef?.current?.value) {
      method.setValue('servicePackageAddOns', []);
    }
    prevDurationRef.current = current;
  }, [durationValue, method]);

  if (isLoading) {
    return <Spin />;
  }

  return (
    <Space direction="vertical" className="u-mt-16 p-packageDetail_generalInfo_wrapper">
      <Row gutter={16}>
        <Col span={12}>
          <Typography.Text strong>{t('package.nameServicePackage')}</Typography.Text>
          <Typography.Text strong type="danger">
            *
          </Typography.Text>
          <Controller
            control={method.control}
            name="name"
            defaultValue=""
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Input
                className="u-mt-8"
                name="name"
                placeholder="Enter package name"
                value={value}
                onChange={onChange}
                error={error?.message}
                size="large"
              />
            )}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Typography.Text strong>{t('package.duration')}</Typography.Text>
          <Typography.Text strong type="danger">
            *
          </Typography.Text>
          <Controller
            control={method.control}
            name="duration"
            defaultValue=""
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DropdownElement
                locale="vi"
                options={packageDurations(t)}
                placeholder="---"
                isGetOption
                value={value}
                isShowSearch
                onChange={onChange}
                error={error?.message}
              />
            )}
          />
        </Col>
      </Row>
      <Row>
        <Typography.Text strong>{t('sidebar.add_on_package')}</Typography.Text>
        <Controller
          control={method.control}
          name="servicePackageAddOns"
          render={({ field }) => (
            <Select
              {...field}
              mode="multiple"
              style={{ width: '100%' }}
              placeholder={`${t('media.select')} ${t('sidebar.add_on_package')}`}
              defaultValue={method.watch('servicePackageAddOns')}
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={addOnsOptions?.map((item) => ({
                label: item.label,
                value: item.value
              }))}
            />
          )}
        />
      </Row>
      <Row>
        <Controller
          control={method.control}
          name="isApplyPayg"
          render={({ field: { value, onChange } }) => (
            <Space>
              <Checkbox
                name="isApplyPayg"
                checked={value}
                onChange={onChange}
              >
                <Typography.Text>
                  {t('package.applyPAYG')}
                </Typography.Text>
              </Checkbox>
            </Space>
          )}
        />
      </Row>
    </Space>
  );
};

export default PackageGeneralInfo;
